import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Home as HomeIcon, DollarSign, Clock } from 'lucide-react';
import SEO from '@/components/SEO';

export default function CaseStudiesIndex() {
  const navigate = useNavigate();

  const caseStudies = [
    {
      id: 'kendall',
      title: 'Kendall Full House',
      subtitle: 'Complete Home Replacement with Good / Better / Premium Options',
      image: 'https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/p1dw6cbv_WhatsApp%20Image%202026-04-17%20at%2012.19.27%20PM.jpeg',
      description: 'How a Kendall homeowner compared Good, Better, and Premium tiers, understood transparent pricing, and chose ES Windows (Premium) for a full 15-window + 3-door replacement without a sales appointment.',
      stats: {
        cost: '$24,500',
        timeline: '~8 weeks',
        type: 'Single-Family Home'
      },
      url: '/case-study-kendall-full-house',
      highlights: [
        'Good / Better / Premium comparison',
        'Transparent pricing process',
        'No pressure, homeowner control',
        '15 windows + 3 French doors'
      ]
    },
    {
      id: 'north-miami',
      title: 'North Miami Waterfront Condo',
      subtitle: 'Coastal Condo with Building Requirements & HOA Approval',
      image: 'https://customer-assets.emergentagent.com/job_impact-quote-hub/artifacts/b9vwpak9_WhatsApp%20Image%202026-04-19%20at%204.43.58%20PM.jpeg',
      description: 'How we navigated HOA approval, coastal requirements, and building restrictions to deliver a compliant Mr. Glass impact system for a waterfront condo unit.',
      stats: {
        cost: '$25,000',
        timeline: '~7 weeks',
        type: 'Waterfront Condo'
      },
      url: '/case-study-north-miami-condo',
      highlights: [
        'HOA approval process handled',
        'Coastal-grade 2605 paint',
        'Building-compliant installation',
        '12 windows + 2 sliding doors'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[var(--brand-ocean-50)]">
      <SEO
        title="Real Impact Window Case Studies — South Florida Projects"
        description="See real Good/Better/Premium impact window projects in South Florida, with real costs, timelines, and homeowner outcomes."
        path="/case-studies"
      />
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-[var(--brand-slate-200)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <button 
            onClick={() => navigate('/')}
            className="text-sm text-[var(--brand-ocean-700)] hover:text-[var(--brand-ocean-900)] flex items-center gap-2"
          >
            ← Back | Home / Case Studies
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[var(--brand-slate-900)] text-center mb-4">
            Real Projects, Real Results
          </h1>
          <p className="text-lg text-[var(--brand-slate-600)] text-center max-w-3xl mx-auto">
            See how we help South Florida homeowners and condo owners protect their properties with transparent pricing, clear options, and no-pressure service.
          </p>
        </div>
      </div>

      {/* Case Studies Grid */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid lg:grid-cols-2 gap-8">
          {caseStudies.map((study) => (
            <Card key={study.id} className="rounded-2xl shadow-xl border-2 border-[var(--brand-ocean-100)] overflow-hidden hover:shadow-2xl transition-shadow">
              <CardContent className="p-0">
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-heading text-2xl font-bold text-white mb-1">{study.title}</h3>
                    <p className="text-sm text-white/90">{study.subtitle}</p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Stats */}
                  <div className="flex gap-4 mb-4 pb-4 border-b border-[var(--brand-slate-200)]">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-[var(--brand-ocean-700)]" />
                      <span className="text-sm font-semibold text-[var(--brand-slate-900)]">{study.stats.cost}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[var(--brand-ocean-700)]" />
                      <span className="text-sm text-[var(--brand-slate-700)]">{study.stats.timeline}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <HomeIcon className="h-4 w-4 text-[var(--brand-ocean-700)]" />
                      <span className="text-sm text-[var(--brand-slate-700)]">{study.stats.type}</span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-[var(--brand-slate-700)] mb-4 leading-relaxed">
                    {study.description}
                  </p>

                  {/* Highlights */}
                  <div className="space-y-2 mb-6">
                    {study.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-[var(--brand-ocean-700)] mt-2 shrink-0"></div>
                        <p className="text-sm text-[var(--brand-slate-600)]">{highlight}</p>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <Button
                    onClick={() => navigate(study.url)}
                    className="w-full bg-[var(--brand-ocean-700)] hover:bg-[var(--brand-ocean-800)] text-white font-semibold rounded-xl h-12"
                  >
                    View Full Case Study <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 bg-gradient-to-br from-[var(--brand-ocean-50)] to-[var(--brand-ocean-100)] rounded-2xl p-8 text-center border-2 border-[var(--brand-ocean-200)]">
          <h3 className="font-heading text-2xl font-bold text-[var(--brand-slate-900)] mb-3">
            Ready to Start Your Project?
          </h3>
          <p className="text-[var(--brand-slate-700)] mb-6">
            Upload photos and receive Good, Better, and Premium pricing options within 24 hours.
          </p>
          <Button
            onClick={() => navigate('/get-quote')}
            className="bg-[var(--brand-ocean-700)] hover:bg-[var(--brand-ocean-800)] text-white font-semibold rounded-xl h-12 px-8"
          >
            Get My Price
          </Button>
          <p className="text-sm text-[var(--brand-slate-600)] mt-3">
            No in-home appointment required. No pressure. Just clear pricing.
          </p>
        </div>
      </div>
    </div>
  );
}
